import {CategoryEntry} from '~/types/Category'
import {createJsonHeaders, getBaseUrl} from '~/utils/fetchHelpers'
import logger from '~/utils/logger'
import {ArtefactOption} from './SoftwareEvaluationArtefact'
import {EvaluationContactPersonOption} from './SoftwareEvaluationPoFTopic'

export type RowId = number

export type SoftwareEvaluationResult = 'counted' | 'not counted' | 'undefined'
export type SoftwareIndicatorReviewStatus = 'pending' | 'in progress' | 'finished'

export type SoftwareQualityEvaluator = {software?: string, id: string, given_names: string, family_names: string, affiliation: string, email?: string}

export type SoftwareEvaluationReportingInformation = {
  organisation_id: string
  organisation_name: string
  organisation_slug: string
  pof_topic: {
    id: string | null,
    name: string | null,
    short_name: string | null,
  }
  contact_person: {
    id: string | null
    name: string | null
  }
}

export type DimensionName = 'Findable' | 'Accessible' | 'Interoperable' | 'Reusable' | 'Scientific basis' | 'Technical basis'

export type SoftwareIndicatorDimension = {
  id: RowId
  name: string
  year: string
  description: string
  minimum_required_level: number
  attributes?: SoftwareIndicatorAttribute[]
}

export type SoftwareIndicatorAttribute = {
  id: RowId
  name: string
  dimension?: SoftwareIndicatorDimension
  weight: number
  levels?: SoftwareIndicatorAttributeLevel[]
}

export type SoftwareIndicatorAttributeLevel = {
  id: RowId
  level: number
  attribute?: SoftwareIndicatorAttribute
  description: string
}

export type SoftwareQualityAttributeLevelEvaluation = {
  id: RowId
  software_id: string
  dimension_id: SoftwareIndicatorDimension['id']
  dimension_name: SoftwareIndicatorDimension['name']
  attribute_id: SoftwareIndicatorAttribute['id']
  attribute_level_id: SoftwareIndicatorAttributeLevel['id']
  level: SoftwareIndicatorAttributeLevel['level']
  fulfilled: boolean | null,
  status: SoftwareIndicatorReviewStatus
  evaluated_at: string
  evaluator: SoftwareQualityEvaluator
}

export type LevelEvaluation = Partial<Record<SoftwareIndicatorAttributeLevel['id'], SoftwareQualityAttributeLevelEvaluation>>
export type AttributeEvaluation = Partial<Record<SoftwareIndicatorAttribute['id'], LevelEvaluation>>
export type SoftwareQualityEvaluation = Partial<Record<SoftwareIndicatorDimension['id'], AttributeEvaluation>>

export type SoftwareAttributeScore = {
  attribute_id: SoftwareIndicatorAttribute['id']
  attribute_score: number
}

export type SoftwareDimensionScore = {
  dimension_id: SoftwareIndicatorDimension['id']
  dimension_score: number
}

export async function getSoftwareIndicatorConfigurationData({year, token}: {year: string, token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/software_indicator_configuration'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        evaluation_year: year
      })
    })
    if (resp.status === 200) {
      const data: SoftwareIndicatorDimension[] = await resp.json()
      return data
    }
    logger(`getSoftwareIndicatorConfigurationData not 200: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getSoftwareIndicatorConfigurationData: ${e?.message}`, 'error')
    return []
  }
}

export async function getSoftwareIndicatorEvaluationPeriods({token}: {token: string}) {
  try {
    const url = getBaseUrl() + '/evaluation_periods'
    const resp = await fetch(url, {
      method: 'GET',
      headers: createJsonHeaders(token),
    })
    if (resp.status === 200) {
      const data: {year: string}[] = await resp.json()
      return data
    }
    logger(`getSoftwareIndicatorEvaluationPeriods not 200: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getSoftwareIndicatorEvaluationPeriods: ${e?.message}`, 'error')
    return []
  }
}

export type SoftwareEvaluationIdentifier = {
  software_id: string
  evaluation_year: SoftwareIndicatorDimension['year']
}

export type PolygonData = Pick<SoftwareIndicatorDimension, 'id' | 'name' | 'minimum_required_level'>

export async function updateMinimalPolygon({polygon, token }: {polygon: Pick<SoftwareIndicatorDimension, 'id' | 'minimum_required_level'>[], token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/update_software_quality_minimal_polygon'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        json_data: polygon
      })
    })
    if (resp.status === 204) {
      return {
        status: 204,
        message: 'Minimal polygon successfully updated'
      }
    } else {
      return {
        status: 400,
        message: 'Bad request when updating minimal polygon'
      }
    }
  } catch (e: any) {
    logger(`updateMinimalPolygon: ${e?.message}`, 'error')
    return {
      status: 500,
      message: e?.message
    }
  }
}

export async function updateSoftwareIndicatorAttributes({token, attributes}: {token: string, attributes: Pick<SoftwareIndicatorAttribute, 'id' | 'weight'>[]}) {
  try {
    const url = getBaseUrl() + '/rpc/update_software_indicator_attributes'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        json_data: attributes
      })
    })
    if (resp.status === 204) {
      return {
        status: 204,
        mesage: 'Software indicator attribute successfully updated.'
      }
    } else {
      return {
        status: 400,
        mesage: 'Bad request when updating software indicator attribute.'
      }
    }
  } catch (e: any) {
    logger(`updateSoftwareIndicatorAttribute: ${e?.message}`, 'error')
    return {
      status: 500,
      message: e?.message
    }
  }
}

export type EvaluationData = {
  id: SoftwareEvaluation['id']
  software: {
    software_slug: string
    software_name: string
    software_id: string
  }
  year: SoftwareIndicatorDimension['year']
  current_artefact: SoftwareEvaluationArtefact | null
  current_reporting_information: SoftwareEvaluationReportingInformation[]
  status_history: ActivityLogRow[]
  current_level_evaluations: SoftwareQualityAttributeLevelEvaluation[] | null
  evaluation_status: {
    id: SoftwareIndicatorDimension['id']
    dimension_score: SoftwareDimensionScore['dimension_score'],
    dimension_status: SoftwareIndicatorReviewStatus,
    attributes: {
      id: SoftwareIndicatorAttribute['id']
      attribute_score: SoftwareAttributeScore['attribute_score']
      attribute_status: SoftwareIndicatorReviewStatus
    }[]
  }[]
  evaluation_result: SoftwareEvaluationResult
}

export async function getSoftwareQualityEvaluationData({software_id, year, token}: {software_id: string, year: string, token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/software_quality_evaluation_data'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        software_id: software_id,
        evaluation_year: year
      })
    })
    if (resp.status === 200) {
      const data: EvaluationData = await resp.json()
      return data
    }
    logger(`getSoftwareQualityEvaluationData not 200: ${resp.status} - ${resp.body}`, 'error')
    return null
  } catch (e: any) {
    logger(`getSoftwareQualityEvaluationData: ${e?.message}`, 'error')
    return null
  }
}

export async function getAllSoftwareQualityAttributeLevelEvaluations({token, software, year, attribute_id}: {token: string, software?: string, year?: string, attribute_id?: number}) {
  try {
    let queryString: string = ''
    if (year || software || attribute_id) {
      const yearQuery = (year !== undefined) ? `year=eq.${year}` : ''
      const softwareQuery = (software !== undefined) ? `software_id=eq.${software}` : ''
      const attributeQuery = (attribute_id !== undefined) ? `attribute_id=eq.${attribute_id}` : ''
      queryString = '?' + yearQuery + (year ? '&' : 'test') + softwareQuery + (year || software ? '&' : '') + attributeQuery
    }
    const url = getBaseUrl() + '/software_level_evaluations' + queryString + '&order=evaluated_at.desc'
    const resp = await fetch(url, {
      method: 'GET',
      headers: createJsonHeaders(token)
    })
    if (resp.status === 200) {
      const data: SoftwareQualityAttributeLevelEvaluation[] = await resp.json()
      return data
    }
    logger(`getAllSoftwareQualityAttributeLevelEvaluations not 200: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getAllSoftwareQualityAttributeLevelEvaluations: ${e?.message}`, 'error')
    return []
  }
}

export async function getLatestSoftwareQualityAttributeLevelEvaluations({software, year, token}: {software: string, year: string, token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/current_attribute_level_evaluations_json'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        software_id: software,
        evaluation_year: year
      })
    })
    if (resp.status === 200) {
      const data: SoftwareQualityEvaluation = await resp.json()
      return data
    }
    logger(`getLatestSoftwareQualityAttributeLevelEvaluations not 200: ${resp.status} - ${resp.body}`, 'error')
    return {}
  } catch (e: any) {
    logger(`getLatestSoftwareQualityAttributeLevelEvaluations: ${e?.message}`, 'error')
    return {}
  }
}

export type SoftwareEvaluationArtefact = {
  artefact_id: string
  software: string
  evaluation_id: string
  artefact: string | null
  last_modified: string
  evaluator: string
}

export async function saveSoftwareEvaluationArtefact({artefact, token}: {artefact: Omit<SoftwareEvaluationArtefact, 'artefact_id' | 'last_modified'>, token: string}) {
  try {
    const url = getBaseUrl() + '/software_quality_evaluation_artefact'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify(artefact),
    })
    if (resp.status !== 204) {
      logger(`saveSoftwareEvaluationArtefact not 204: ${resp.status} - ${resp.body}`)
    }
  } catch (e: any) {
    logger(`saveSoftwareEvaluationArtefact: ${e?.message}`, 'error')
  }
}

export async function saveSoftwareEvaluationPoFTopic({data, token}: {data: any, token: string}) {
  try {
    const url = getBaseUrl() + '/software_quality_evaluation_pof_topics'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify(data),
    })
    if (resp.status !== 204) {
      logger(`saveSoftwareEvaluationOrganisationInformation not 204: ${resp.status} - ${resp.body}`)
    }
  } catch (e: any) {
    logger(`saveSoftwareEvaluationOrganisationInformation: ${e?.message}`, 'error')
  }
}

export type ContactPersonTableSchema = {
  id?: string
  software: string
  evaluation_id: string
  organisation: string
  contact_person: string | null
  contact_person_id?: string
  changed_at?: string
  changed_by: string
}

export async function saveSoftwareEvaluationContactPerson({data, token}: {data: ContactPersonTableSchema, token: string}) {
  try {
    const url = getBaseUrl() + '/software_quality_evaluation_contact_person'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify(data),
    })
    if (resp.status !== 204) {
      logger(`saveSoftwareEvaluationContactPerson not 204: ${resp.status} - ${resp.body}`)
    }
  } catch (e: any) {
    logger(`saveSoftwareEvaluationContactPerson: ${e?.message}`, 'error')
  }
}

export type SoftwareEvaluation = {
  id: string
  software: string
  year: string
}

export type SoftwareEvaluationAction = 'create' | 'submit' | 'reset submit' | 'lock' | 'unlock' | 'update submit_ready' | 'update artefact' | 'update pof topic' | 'update contact person'

export type SoftwareEvaluationStatus = {
  id: string
  evaluation_id: SoftwareEvaluation['id']
  submit_ready?: boolean
  submitted?: boolean
  locked?: boolean
  artefact_id?: SoftwareEvaluationArtefact['artefact_id']
  changed_at?: string
  changed_by?: string
  action: SoftwareEvaluationAction
}

export type SoftwareEvaluationData = Omit<SoftwareEvaluation, 'software'> & Partial<Omit<SoftwareEvaluationStatus, 'id'>> & {
  // additional props from view
  software_slug?: string
  software_name?: string
  software_id?: string
  evaluation_result?: SoftwareEvaluationResult
}

export async function saveSoftwareQualityEvaluation({evaluation, token }: {evaluation: Omit<SoftwareEvaluation, 'id'>, token: string}) {
  try {
    const url = getBaseUrl() + '/software_quality_evaluation'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify(evaluation)
    })
    if (resp.status === 200 || resp.status === 201) {
      return '200'
    }
    logger(`saveSoftwareQualityEvaluation not 200/201: ${resp.status} - ${resp.body}`, 'error')
    return '300'
  } catch (e: any) {
    logger(`saveSoftwareQualityEvaluation: ${e?.message}`, 'error')
    return '500'
  }
}

export async function submitEvaluation({evaluation, token}:{evaluation: SoftwareEvaluationIdentifier, token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/submit_software_evaluation'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify(evaluation),
    })
    if (resp.status === 204) {
      return '200'
    }
    logger(`submitEvaluation not 200/201: ${resp.status} - ${resp.body}`, 'error')
    return '300'
  } catch (e: any) {
    logger(`submitEvaluation: ${e?.message}`, 'error')
    return '500'
  }
}

export async function getSoftwareQualityEvaluations({software, year, token}: {software: string[], year?: string, token: string}) {
  try {
    const url = getBaseUrl() + `/software_quality_evaluations_view?software_id=in.(${software.toString()})` + (year ? `&year=eq.${year}` : '')
    const resp = await fetch(url, {
      method: 'GET',
      headers: createJsonHeaders(token)
    })
    if (resp.status === 200) {
      const data: SoftwareEvaluationData[] = await resp.json()
      return data
    }
    logger(`getSoftwareQualityEvaluations not 200: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getSoftwareQualityEvaluations: ${e?.message}`, 'error')
    return []
  }
}

type SaveLevelEvaluationProps = {
  software: string
  evaluation_id: SoftwareEvaluation['id']
  attribute_level: SoftwareIndicatorAttributeLevel['id']
  fulfilled: boolean | null
  evaluator: string
}

export async function saveSoftwareQualityAttributeLevelEvaluation({levelEvaluation, token}: {levelEvaluation: SaveLevelEvaluationProps, token: string}) {
  try {
    const url = getBaseUrl() + '/software_quality_attribute_level_evaluation'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify(levelEvaluation)
    })
    if (resp.status === 201 || resp.status === 200) {
      return '200'
    }
    logger(`saveSoftwareQualityAttributeLevelEvaluation not 200: ${resp.status} - ${resp.body}`, 'error')
    return '300'
  } catch (e: any) {
    logger(`saveSoftwareQualityAttributeLevelEvaluation: ${e?.message}`, 'error')
    return '500'
  }
}

export async function getAllEvaluatedSoftwareEntries({token}: {token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/software_quality_evaluated_software'
    const resp = await fetch(url, {
      method: 'GET',
      headers: createJsonHeaders(token)
    })
    if (resp.status === 200) {
      const data: {software_id: string, evaluation_year: string, software_slug: string, software_name: string}[] = await resp.json()
      return data
    }
    logger(`getAllEvaluatedSoftwareEntries not 200: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getAllEvaluatedSoftwareEntries: ${e?.message}`, 'error')
    return []
  }
}

export async function getEvaluatedSoftwareEntriesForOrganisation({organisation, token}: {organisation: string, token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/organisation_software_quality_evaluated_software'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        organisation_id: organisation
      })
    })
    if (resp.status === 200) {
      const data: {software_id: string, evaluation_year: string, software_slug: string, software_name: string}[] = await resp.json()
      return data
    }
    logger(`getEvaluatedSoftwareEntriesForOrganisation not 200: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getEvaluatedSoftwareEntriesForOrganisation: ${e?.message}`, 'error')
    return []
  }
}

export async function getSoftwareEvaluatorsForMultipleSoftwareEntries({softwareEvaluations, token}: {softwareEvaluations: SoftwareEvaluationIdentifier[], token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/software_quality_evaluators_bulk'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        evaluation_items: softwareEvaluations
      })
    })
    if (resp.status === 200) {
      const data: SoftwareQualityEvaluator[] = await resp.json()
      return data
    }
    logger(`getSoftwareEvaluators not 200: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getSoftwareEvaluators: ${e?.message}`, 'error')
    return []
  }
}

export async function getAttributeReviewStatusForMultipleSoftwareEntries({softwareEvaluations, token}: {softwareEvaluations: SoftwareEvaluationIdentifier[], token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/calculate_attribute_review_status_bulk'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        evaluation_items: softwareEvaluations
      })
    })
    if (resp.status === 200) {
      const data: {software_id: string, attribute_name: string, attribute_review_status: string}[] = await resp.json()
      return data
    }
    logger(`getAttributeReviewStatusForMultipleSoftwareEntries not 200: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getAttributeReviewStatusForMultipleSoftwareEntries: ${e?.message}`, 'error')
    return []
  }
}

export async function getDimensionReviewStatusForMultipleSoftwareEntries({softwareEvaluations, token}: {softwareEvaluations: SoftwareEvaluationIdentifier[], token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/calculate_dimension_review_status_bulk'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        evaluation_items: softwareEvaluations
      })
    })
    if (resp.status === 200) {
      const data: {software_id: string, dimension_name: string, dimension_review_status: string}[] = await resp.json()
      return data
    }
    logger(`getDimensionReviewStatusForMultipleSoftwareEntries not 200: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getDimensionReviewStatusForMultipleSoftwareEntries: ${e?.message}`, 'error')
    return []
  }
}

export async function getEvaluationResultForMultipleSoftwareEntries({softwareEvaluations, token}: {softwareEvaluations: SoftwareEvaluationIdentifier[], token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/get_software_evaluation_result_bulk'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        evaluation_items: softwareEvaluations
      })
    })
    if (resp.status === 200) {
      const data: {software_id: string, software_evaluation_status: SoftwareEvaluationResult}[] = await resp.json()
      return data
    }
    logger(`getEvaluationResultForMultipleSoftwareEntries not 200: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getEvaluationResultForMultipleSoftwareEntries: ${e?.message}`, 'error')
    return []
  }
}

export async function getPoFTopicsForMultipleSoftwareEntries({softwareIds, token}: {softwareIds: string[], token: string}) {
  try {
    const url = getBaseUrl() + `/category_for_software?software_id=in.(${softwareIds})&select=software_id,category(id),category(name)`
    const resp = await fetch(url, {
      method: 'GET',
      headers: createJsonHeaders(token),
    })
    if (resp.status === 200) {
      const data: {software_id: string, software_evaluation_status: SoftwareEvaluationResult}[] = await resp.json()
      return data
    }
    logger(`getPoFTopicsForMultipleSoftwareEntries not 200: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getPoFTopicsForMultipleSoftwareEntries: ${e?.message}`, 'error')
    return []
  }
}

export type PoFTopicNode = Partial<CategoryEntry> & Pick<CategoryEntry, 'name'> & {
  children: PoFTopicNode[]
}

export async function getAllPoFTopics({token}: {token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/get_pof_topic_tree'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        pof_period: 'Helmholtz PoF IV'
      })
    })
    if (resp.status === 200) {
      const data: PoFTopicNode = await resp.json()
      return data
    }
    logger(`getAllPoFTopics not 200: ${resp.status} - ${resp.body}`, 'error')
    return null
  } catch (e: any) {
    logger(`getAllPoFTopics: ${e?.message}`, 'error')
    return null
  }
}

export async function checkIfIsHelmholtzSoftware({software, token}: {software: string, token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/is_helmholtz_software'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        software_id: software
      })
    })
    if (resp.status === 200) {
      const data: boolean = await resp.json()
      return data
    }
    logger(`checkIfIsHelmholtzSoftware not 200: ${resp.status} - ${resp.body}`, 'error')
    return false
  } catch (e: any) {
    logger(`checkIfIsHelmholtzSoftware: ${e?.message}`, 'error')
    return false
  }
}

export type ActivityLogRow = SoftwareEvaluationStatus & {
  given_names: string
  family_names: string
  affiliation: string
  artefact: string
  pof_topic: string,
  contact_person: string | null,
  organisation: string
}

export async function getReleasesForSoftwareEvaluation(software:string, year: string, token?:string) {
  try{
    const query = `select=release(mention(doi,version,publication_year))&id=eq.${software}&release.mention.publication_year=eq.${year}&release.mention.order=doi_registration_date.desc`
    const url = `${getBaseUrl()}/software?${query}`
    const resp = await fetch(url, {
      method: 'GET',
      headers: createJsonHeaders(token)
    })
    if (resp.status===200){
      const data: any[] = await resp.json()
      if (data.length === 1 && data[0]?.release?.mention) {
        const releases: ArtefactOption[] = data[0]['release']['mention']
        return releases
      }
      return null
    } else if (resp.status===404){
      logger(`getReleasesForSoftwareEvaluation: 404 [${url}]`,'error')
      return null
    }
    return null
  }catch(e:any){
    logger(`getReleasesForSoftwareEvaluation: ${e?.message}`,'error')
    return null
  }
}

export async function getContributorsForSoftwareEvaluation(software: string, token?: string) {
  try{
    const url = getBaseUrl() + `/contributor?software=eq.${software}&select=given_names,family_names,account`
    const resp = await fetch(url, {
      method: 'GET',
      headers: createJsonHeaders(token)
    })
    if (resp.status===200){
      const data: {given_names: string, family_names: string, account: string}[] = await resp.json()
      const contributors: EvaluationContactPersonOption[] = data.map((row) => {return {type: 'Contributor', label: row.given_names + ' ' + row.family_names, id: row.account}})
      return contributors
    } else if (resp.status===404){
      logger(`getContributorsForSoftwareEvaluation: 404 [${url}]`,'error')
      return []
    }
    return []
  }catch(e:any){
    logger(`getContributorsForSoftwareEvaluation: ${e?.message}`,'error')
    return []
  }
}

export async function getGeneralIndicatorMetrics(token?: string) {
  try{
    const url = getBaseUrl() + '/rpc/get_general_indicator_metrics'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      cache: 'no-store'
    })
    if (resp.status===200){
      const data: {evaluations_started: number, centres_associated: number, pof_topics_associated: number} = await resp.json()
      return data
    } else if (resp.status===404){
      logger(`getGeneralIndicatorMetrics: 404 [${url}]`,'error')
      return null
    }
    return null
  }catch(e:any){
    logger(`getGeneralIndicatorMetrics: ${e?.message}`,'error')
    return null
  }
}

